import { useEffect, useState } from 'react';

import {
  BtnToolbar,
  Button,
  ButtonAction,
  Combobox,
  type HookFormMethods,
  HookFormResolver,
  Text,
  TextArea
} from '@dec/shared';
/* eslint-disable react/display-name */
import { Alert, ButtonGroup, Col, Row } from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { catalogs } from '../../../Parameters/Catalog/Catalog/catalog';
import { type CatalogModel } from '../../../Parameters/Catalog/Catalog/types';
import {
  useCatalogList,
  useCatalogListByParent
} from '../../../Parameters/Catalog/Catalog/useCatalog';
import { label as lblCommon } from '../../../translation';
import { useTaxpayerRequestStore } from '../../stores/useTaxpayerRequestStore';
import { GivenNamesAndSurNames } from '../components/given-names-and-sur-names';
import ProgressBar from '../components/progress-bar';
import { ValidationsResolver } from '../rules';
import { rulesContactData } from '../rules/contact';
import { label } from '../translation';
import VisibleComponent from '../visible-component';

import { type ContactModel, type ProgressStepProps } from './types';
interface ContactProps {
  onSubmit: (data: any) => void;
  contacts: Array<ContactModel>;
  readonly?: boolean;
  modeChange?: (editMode: boolean) => void;
}

export const Contact = ({ modeChange, onSubmit, contacts, readonly }: ContactProps) => {
  const { t, i18n } = useTranslation();
  const [parent, setParent] = useState('');
  const [show, setShow] = useState(false);
  const addContactState = useTaxpayerRequestStore((s) => s.addContact);
  const removeContact = useTaxpayerRequestStore((s) => s.removeContact);
  useEffect(() => {
    setShow(true);
  }, []);
  const [taxpayer, setTaxpayer] = useState<ContactModel>({});
  const [countryTaxpayer, setCountry] = useState<CatalogModel>();
  const [{ data: dataPerson, loading: loadingPerson, error: errorPerson }] = useCatalogList(
    catalogs.personTypes
  );
  const [{ data: dataCountry, loading: loadingCountry, error: errorCountry }] = useCatalogList(
    catalogs.countries
  );
  const [{ data: pdData, loading: pdLoading, error: pdError }] = useCatalogListByParent(
    catalogs.politicalDivisions,
    parent
  );

  const changeToEditMode = (editMode: boolean) => {
    setShow(editMode);
    if (modeChange) {
      modeChange(editMode);
    }
  };

  const newContact = () => {
    setTaxpayer({});
    setCountry({} as CatalogModel);
    //setShow(false);
    changeToEditMode(false);
  };

  const addContact = (contact: ContactModel, reset: any) => {
    if (contacts) {
      // contact.personType = contact.personType ? contact.personType.id : null;
      //contact.country = contact.country ? contact.country.id : null;
      //contact.politicalDivision = contact.politicalDivision ? contact.politicalDivision.id : null;
      contact.index = taxpayer?.index ? taxpayer?.index : contacts.length + 1;
      contact.rowId = taxpayer.rowId ? taxpayer.rowId : uuidv4();

      addContactState(contact);
      setTaxpayer({});
      setCountry({} as CatalogModel);
      reset({ personType: null, country: null, politicalDivision: null });
      // setShow(true);
      // if (modeChange) {
      //   modeChange(true);
      // }
      changeToEditMode(true);
    }
  };

  return (
    <>
      {!show && (
        <ValidationsResolver rules={rulesContactData}>
          {({ validations }: any) => {
            return (
              <HookFormResolver
                nameForm={'formContactData'}
                validations={validations}
                onSubmit={addContact}
                initialValues={
                  taxpayer?.localization ? { localization: taxpayer?.localization } : undefined
                }
              >
                {({
                  formState: { errors },
                  reset,
                  setValue,
                  getValues,
                  watch
                }: HookFormMethods) => {
                  const personType = watch('personType');

                  return (
                    <>
                      <hr />
                      <Row>
                        <Col>
                          <Combobox
                            defaultValue={taxpayer.personType}
                            label={lblCommon('personType')}
                            name="personType"
                            data={!errorPerson ? dataPerson : []}
                            code="id"
                            display="name"
                            readOnly={readonly}
                          />
                        </Col>
                      </Row>
                      <GivenNamesAndSurNames
                        enable={personType?.code === 'individual'}
                        readonly={readonly}
                        defaultValues={{
                          givenNames: taxpayer?.givenNames,
                          surnames: taxpayer?.surnames
                        }}
                        propetyName="taxpayerName"
                      />
                      <Row>
                        <Col>
                          <Text
                            name="taxpayerName"
                            label={
                              personType?.code === 'business'
                                ? label(t, 'businessName')
                                : label(t, 'fullName')
                            }
                            defaultValue={taxpayer.taxpayerName}
                            readonly={readonly || personType?.code === 'individual'}
                          />
                        </Col>
                      </Row>
                      {personType?.code === 'business' && (
                        <Row>
                          <Col>
                            <Text
                              readonly={readonly}
                              name="tradeName"
                              label={lblCommon('tradeName')}
                              defaultValue={taxpayer.tradeName}
                            />
                          </Col>
                        </Row>
                      )}
                      <Row>
                        {/* solamente se debe pedir el numero de identificación del contacto
                        si se trata de una persona física */}
                        {personType?.code === 'individual' && (
                          <VisibleComponent fieldCode="contactIdentification">
                            <Col>
                              <Text
                                defaultValue={taxpayer.identification}
                                label={label(t, 'id')}
                                name="identification"
                                readonly={readonly}
                              />
                            </Col>
                          </VisibleComponent>
                        )}

                        <VisibleComponent fieldCode="contactTaxNumber">
                          <Col>
                            <Text
                              defaultValue={taxpayer.taxNumber}
                              label={label(t, 'taxNumber')}
                              name="taxNumber"
                              readonly={readonly}
                            />
                          </Col>
                        </VisibleComponent>
                      </Row>
                      <Row>
                        <VisibleComponent fieldCode="contactCountry">
                          <Col>
                            <Combobox
                              code="id"
                              readOnly={readonly}
                              display="name"
                              data={!errorCountry ? dataCountry : []}
                              label={t('common.label.country')}
                              name="country"
                              defaultValue={taxpayer.country}
                              child="politicalDivision"
                            />
                          </Col>
                        </VisibleComponent>
                        <VisibleComponent fieldCode="contactPoliticalDivision">
                          <Col>
                            <Combobox
                              code="id"
                              readOnly={readonly}
                              display="name"
                              defaultValue={taxpayer.politicalDivision}
                              data={!pdError ? pdData : []}
                              label={t('common.label.politicalDivision')}
                              name="politicalDivision"
                            />
                          </Col>
                        </VisibleComponent>
                      </Row>
                      <Row>
                        <VisibleComponent fieldCode="contactCity">
                          <Col>
                            <Text
                              defaultValue={taxpayer.city}
                              label={t('common.label.city')}
                              name="city"
                              readonly={readonly}
                            />
                          </Col>
                        </VisibleComponent>
                        <VisibleComponent fieldCode="contactZipCode">
                          <Col>
                            <Text
                              defaultValue={taxpayer.zipCode}
                              label={t('common.label.zipCode')}
                              name="zipCode"
                              readonly={readonly}
                            />
                          </Col>
                        </VisibleComponent>
                      </Row>
                      <VisibleComponent fieldCode="contactAddress">
                        <Row>
                          <Col>
                            <TextArea
                              defaultValue={taxpayer.address}
                              rows={3}
                              label={t('common.label.address')}
                              name="address"
                              readonly={readonly}
                            />
                          </Col>
                        </Row>
                      </VisibleComponent>

                      <Row>
                        <Col className="mt-auto">
                          <Text
                            defaultValue={taxpayer.email}
                            label={t('common.label.email')}
                            name="email"
                            readonly={readonly}
                          />
                        </Col>
                        <VisibleComponent fieldCode="contactPhoneNumber">
                          <Col>
                            <div className="d-flex">
                              <div className="w-25 me-2 mt-auto">
                                <Text
                                  defaultValue={taxpayer.phoneNumberArea}
                                  readonly={readonly}
                                  name="phoneNumberArea"
                                  label={t('common.label.phoneNumberArea')}
                                />
                              </div>
                              <div className="w-75">
                                <Text
                                  defaultValue={taxpayer.phoneNumber}
                                  label={t('common.label.phoneNumber')}
                                  name="phoneNumber"
                                  readonly={readonly}
                                />
                              </div>
                            </div>
                          </Col>
                        </VisibleComponent>
                      </Row>
                      <hr></hr>
                      <BtnToolbar>
                        <ButtonGroup>
                          <Button
                            action={readonly ? ButtonAction.return : ButtonAction.cancel}
                            onClick={() => changeToEditMode(true)}
                          />
                        </ButtonGroup>
                        {!readonly && (
                          <ButtonGroup>
                            {/*<Button action={ButtonAction.new} onClick={() => newContact(reset)} />*/}
                            <Button
                              variant="primary"
                              action={taxpayer.index ? ButtonAction.save : ButtonAction.add}
                              type={'submit'}
                            />
                          </ButtonGroup>
                        )}
                      </BtnToolbar>
                    </>
                  );
                }}
              </HookFormResolver>
            );
          }}
        </ValidationsResolver>
      )}
      {show && (
        <>
          <BtnToolbar cssClass="mb-2">
            <ButtonGroup />
            <ButtonGroup>
              <Button
                action={ButtonAction.new}
                text={t('taxpayer-request.label.addContact')}
                onClick={() => newContact()}
              />
            </ButtonGroup>
          </BtnToolbar>
          {/* acá se listan los contactos registrados */}
          {contacts.length == 0 && (
            <Alert variant="info">
              <div className="d-flex align-items-start gap-3 mt-0 mb-4">
                <div className="pt-1">
                  <Icon.PersonLinesFill size={35} className="text-primary" />
                </div>
                <div>
                  <div className="fw-bolder text-primary">
                    {' '}
                    {t('taxpayer-request.label.contacts')}
                  </div>
                  <div className="text-dark"> {t('taxpayer-request.messages.noContacts')}</div>
                </div>
              </div>
            </Alert>
          )}

          <div className="row">
            {contacts.map((contact) => (
              <div className="col-12 col-sm-6 col-lg-4" key={contact.index}>
                <div className="card">
                  <div className="card-header">
                    <div className="d-flex align-items-center">
                      <Icon.PersonLinesFill size={35} className="text-primary" />
                      <h4 className="ms-4 mb-1 fw-bold">{contact.taxpayerName}</h4>
                    </div>
                  </div>

                  <div className="card-body">
                    <div>
                      <div className="text-muted small">Email</div>
                      <div className="text-decoration-none fs-5">{contact.email}</div>{' '}
                    </div>
                    <div>
                      <div className="text-muted small">Phone</div>
                      <div className="text-decoration-none fs-5">
                        ({contact.phoneNumberArea}) {contact.phoneNumber}
                      </div>
                    </div>
                  </div>

                  <div className="card-footer">
                    <div className="d-flex justify-content-between">
                      {readonly && (
                        <Button
                          variant="secondary"
                          action={ButtonAction.show}
                          className="btn-sm"
                          onClick={() => {
                            changeToEditMode(false);
                            setTaxpayer(contact);
                            setCountry(contact?.country);
                          }}
                        />
                      )}
                      {!readonly && (
                        <Button
                          variant="secondary"
                          action={ButtonAction.edit}
                          className="btn-sm"
                          onClick={() => {
                            changeToEditMode(false);
                            setTaxpayer(contact);
                            setCountry(contact?.country);
                          }}
                        />
                      )}
                      {!readonly && (
                        <Button
                          variant="danger"
                          outline={true}
                          action={ButtonAction.delete}
                          className="btn-sm ms-3"
                          onClick={() => {
                            removeContact(contact);
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
};

export default (props: ProgressStepProps) => {
  const [dirty, setDirty] = useState(false);
  const [showButtons, setShowButtons] = useState(true);
  const { t, i18n } = useTranslation();
  const taxpayer = useTaxpayerRequestStore((s) => s.form.contacts);

  const onSubmit = () => {
    props.nextStep();
  };

  const modeChange = (editMode: boolean) => {
    setShowButtons(editMode);
  };

  return (
    <>
      <Contact modeChange={modeChange} contacts={taxpayer ? taxpayer : []} onSubmit={onSubmit} />
      <ProgressBar percentage={props.percentage} />
      {showButtons && (
        <BtnToolbar>
          <ButtonGroup>
            <Button
              form="formTextData"
              action={ButtonAction.previous}
              onClick={() => props.previousStep()}
            />
          </ButtonGroup>
          <ButtonGroup>
            <Button action={ButtonAction.next} onClick={() => onSubmit()} variant="primary" />
          </ButtonGroup>
        </BtnToolbar>
      )}
    </>
  );
};
